import React, { useState } from 'react';
import { Typography, theme } from 'antd';
import { formatDay, percent } from './GeoExposureLayout';

const { Text } = Typography;

const WIDTH = 720;
const HEIGHT = 220;
const PAD = { top: 16, right: 24, bottom: 36, left: 44 };

/**
 * Share of answers that mentioned us, one point per finished run. A single
 * series, so the title names it and there is no legend; hovering a point
 * shows the run's numbers.
 */
export const MentionChart = ({ series, onSelectRun }) => {
    const [hover, setHover] = useState(null);
    const { token } = theme.useToken();
    const LINE = token.colorPrimary;
    const GRID = token.colorBorderSecondary;
    const AXIS = token.colorTextSecondary;
    if (!series.length) {
        return <Text type="secondary">No finished runs yet.</Text>;
    }
    const innerW = WIDTH - PAD.left - PAD.right;
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const x = (i) => PAD.left + (series.length === 1 ? innerW / 2 : (i / (series.length - 1)) * innerW);
    const y = (share) => PAD.top + innerH - share * innerH;
    const path = series.map((s, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(s.mentionedShare).toFixed(1)}`).join(' ');
    const ticks = [0, 0.25, 0.5, 0.75, 1];
    const labelEvery = Math.max(1, Math.ceil(series.length / 8));
    const hovered = hover == null ? null : series[hover];

    return (
        <div style={{ position: 'relative', maxWidth: WIDTH }}>
            <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Share of answers mentioning Design With Friends per run">
                {ticks.map((t) => (
                    <g key={t}>
                        <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(t)} y2={y(t)} stroke={GRID} />
                        <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill={AXIS}>{Math.round(t * 100)}%</text>
                    </g>
                ))}
                {series.map((s, i) => (i % labelEvery === 0 || i === series.length - 1) && (
                    <text key={s.runId} x={x(i)} y={HEIGHT - PAD.bottom + 18} textAnchor="middle" fontSize={11} fill={AXIS}>{formatDay(s.at)}</text>
                ))}
                <path d={path} fill="none" stroke={LINE} strokeWidth={2} strokeLinejoin="round" />
                {series.map((s, i) => (
                    <g key={s.runId} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => onSelectRun?.(s.runId)} style={{ cursor: 'pointer' }}>
                        <circle cx={x(i)} cy={y(s.mentionedShare)} r={14} fill="transparent" />
                        <circle cx={x(i)} cy={y(s.mentionedShare)} r={hover === i ? 6 : 4} fill={token.colorBgContainer} stroke={LINE} strokeWidth={2} />
                    </g>
                ))}
            </svg>
            {hovered && (
                <div style={{
                    position: 'absolute', left: `${(x(hover) / WIDTH) * 100}%`, top: 0, transform: 'translate(-50%, -8px)',
                    background: token.colorBgElevated, color: token.colorText, border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadius, padding: '6px 10px', fontSize: 12, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: token.boxShadowSecondary,
                }}>
                    <div><strong>{percent(hovered.mentionedShare)}</strong> mentioned</div>
                    <div>{hovered.mentioned} of {hovered.completed} answers · {formatDay(hovered.at)}</div>
                </div>
            )}
        </div>
    );
};
